import {
  ModGeneralAvatar,
  ModIconButton,
  ModTooltip
} from '@moderneinc/design-system-components';
import { semanticColors, spacing } from '@moderneinc/design-system-tokens';
import { Box, Stack, useMediaQuery, useTheme } from '@mui/material';
import { bindToggle } from 'material-ui-popup-state/hooks';
import { useSession } from 'next-auth/react';
import React, { type FunctionComponent, useEffect } from 'react';
import { getSessionAvatarProps } from '../../../helpers/avatar.helper';
import { useManagedPopoverState } from '../../../hooks/use-managed-popover-state.hook';
import { useMenuTriggerTooltip } from '../../../hooks/use-menu-trigger-tooltip.hook';
import { AdminMenuPopover } from '../../navigation/admin-menu/admin-menu-popover.component';
import { HelpMenuPopover } from '../../navigation/help-menu/help-menu-popover.component';
import { GlobalNavigationPopover } from '../global-navigation-popover/global-navigation-popover.component';
import { GlobalActionButtonsAccountMenuPopover } from './global-action-buttons-account-menu-popover.component';

export const GlobalActionButtons: FunctionComponent = () => {
  const accountPopupState = useManagedPopoverState({
    popupId: 'account-menu-global'
  });
  const helpPopupState = useManagedPopoverState({
    popupId: 'help-menu-global'
  });

  const accountTooltipProps = useMenuTriggerTooltip(accountPopupState.isOpen);

  const theme = useTheme();
  const isMdOrLarger = useMediaQuery(theme.breakpoints.up('md'));

  const { isOpen: isHelpOpen, close: closeHelp } = helpPopupState;
  const { isOpen: isAccountOpen, close: closeAccount } = accountPopupState;

  useEffect(() => {
    if (!isMdOrLarger && isHelpOpen) {
      closeHelp();
    }
  }, [isMdOrLarger, isHelpOpen, closeHelp]);

  useEffect(() => {
    if (!isMdOrLarger && isAccountOpen) {
      closeAccount();
    }
  }, [isMdOrLarger, isAccountOpen, closeAccount]);

  const { data: session } = useSession();
  const { avatarSource, avatarInitials, alt } = getSessionAvatarProps(session);
  return (
    <Stack
      direction="row"
      sx={{
        alignItems: 'center',
        gap: `${spacing.spacing_1_2}px`,
        py: `${spacing.spacing_1_2}px`,
        borderRadius: 64
      }}
    >
      {/* Admin menu - always visible for admins */}
      <AdminMenuPopover />
      {/* Help menu - show on medium screens and up */}
      <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
        <HelpMenuPopover popoverState={helpPopupState} />
      </Box>
      {/* User account - show on medium screens and up */}
      {session?.user && (
        <React.Fragment>
          <ModTooltip title="Profile & settings" {...accountTooltipProps}>
            <ModIconButton
              {...bindToggle(accountPopupState)}
              aria-label="Profile & settings"
              variant="nav"
              active={accountPopupState.isOpen}
              sx={{
                display: { xs: 'none', md: 'flex' },
                padding: 0,
                border: 'none',
                backgroundColor: 'transparent',
                '&:hover': {
                  backgroundColor: 'transparent',
                  border: 'none'
                }
              }}
            >
              {/*
               * ModGeneralAvatar rather than ModNavigationAvatar: the latter
               * nests the avatar in a padded, bordered container, which reads
               * as a thick ring. ModIconButton already supplies the
               * interactive area, so the image fills the whole circle.
               * 34px has no spacing token (the scale steps 32 -> 40).
               */}
              <ModGeneralAvatar
                src={avatarSource}
                alt={alt}
                sx={{
                  width: 34,
                  height: 34,
                  boxSizing: 'border-box',
                  border: `1px solid ${semanticColors.color.border.strong}`
                }}
              >
                {avatarInitials}
              </ModGeneralAvatar>
            </ModIconButton>
          </ModTooltip>
          <GlobalActionButtonsAccountMenuPopover
            popoverState={accountPopupState}
          />
        </React.Fragment>
      )}
      {/* Mobile navigation popover - self-contained, handles its own visibility */}
      <GlobalNavigationPopover />
    </Stack>
  );
};
